import type { Locale } from 'date-fns';

import { eachDayOfInterval, endOfWeek, format, startOfWeek } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
// eslint-disable-next-line import-x/order -- oxfmt sorts 'date-fns-tz' before 'date-fns/locale' (ASCII: '-' < '/')
import { enAU } from 'date-fns/locale';

export function getWeekDays(
  { locale }: { locale: Locale },
  pattern: string,
): string[] {
  const now = new Date();
  const weekDays: string[] = [];
  const start = startOfWeek(now, { locale });
  const end = endOfWeek(now, { locale });
  eachDayOfInterval({ start, end }).forEach((day) => {
    weekDays.push(format(day, pattern, { locale }));
  });
  return weekDays;
}

export type WeekStart = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export function getWeekStart({ locale }: { locale: Locale }): WeekStart {
  if (locale.options?.weekStartsOn == null) return 1;
  const weekStart = locale.options.weekStartsOn;
  // const weekStart = getDay(startOfWeek(new Date(), { locale: locale }));
  return weekStart;
}

export function checkValidDate(date: unknown): boolean {
  return date instanceof Date && !isNaN(date.getTime());
}

// TODO refactor: usePickerField.ts `const calendarLocale = enAU`, src/utils/date.ts `locale: enAU`, src/composables/useCurrentDateString.ts
export function getCurrentWeekDates(): [Date, Date] {
  const date = new Date();
  const start = startOfWeek(date, { locale: enAU });
  const end = endOfWeek(date, { locale: enAU });
  return [start, end];
}

export function getZonedDate(utcDate: Date, userTimeZone: string): Date {
  const date = formatInTimeZone(utcDate, userTimeZone, 'yyyy-MM-dd');
  return new Date(Date.parse(date + 'T00:00:00.000'));
}

export function getISODateString(date: Date): string {
  return date.toISOString().slice(0, 10);
}

type DateTimeFormatOptions = Intl.DateTimeFormatOptions;
type timeZoneName = DateTimeFormatOptions['timeZoneName'];

/**
 * Get the UTC offset for a given time zone.
 * @param timeZone - The time zone to get the offset for.
 * @param zoneFormat - The format of the offset to return.
 * @returns The UTC offset for the given time zone.
 */
export function getUTCOffset(
  timeZone: string,
  zoneFormat: timeZoneName,
): string {
  const now = new Date();
  const options: DateTimeFormatOptions = {
    timeZoneName: zoneFormat, // 'short' for (PST, AEST); "shortOffset" (e.g., GMT-8)
    timeZone,
    hour12: false,
  };
  const formatter = new Intl.DateTimeFormat('en-AU', options);
  const parts = formatter.formatToParts(now);
  const offsetPart = parts.find((part) => part.type === 'timeZoneName');
  return offsetPart ? offsetPart.value : '';
}

export function isWorkingHours(
  hours: { start: number; end: number },
  timeZone: string,
): boolean {
  const now = new Date();
  const options: DateTimeFormatOptions = {
    timeZone,
    hour12: false,
    hour: 'numeric',
  };
  const formatter = new Intl.DateTimeFormat('en-AU', options);
  const hour = parseInt(formatter.format(now));
  return hour >= hours.start && hour < hours.end;
}
